import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { SdpResponse, headPointPhysicalMeter } from '../../../../models/SdpResponse';

import { ReactComponent as BarChartIcon } from '../../../../assets/chart-bar.svg';
import { ConsumptionUnitEnum } from '../../../../enum/ConsumptionUnitEnum';
import { DateTime } from 'luxon';
import EmptyState from '../../../common/EmptyState';
import HgChart from '../../../common/HgChart';
import { ReactComponent as InformationIcon } from '../../../../assets/information.svg';
import LastUpdateData from '../../../common/LastUpdateData';
import ModalForecastInformation from '../../../modals/ModalForecastInformation';
import { NumberUtils } from '../../../../common/numberUtils';
import { PeriodEnum } from '../../../../enum/PeriodEnum';
import PeriodGranularity from '../PeriodGranularity';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';
import { useGetBudgetByPeriod } from '../../../../hooks/budgets';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { userPreferencesState } from '../../../../atoms/userPreferencesAtom/userPreferencesAtom';

export interface ForecastCircularWidgetProps {
  sdp?: SdpResponse | null;
  period: PeriodEnum;
  unit: ConsumptionUnitEnum;
}
export default function ForecastCircularWidget({ sdp, period, unit }: ForecastCircularWidgetProps) {
  const { data: budget, isFetching, isError } = useGetBudgetByPeriod(sdp?.id, unit, period);
  const [fullyExceededBudget, setFullyExceededBudget] = useState(false);
  const [exceededBudget, setExceededBudget] = useState(false);
  const [referenceAmount, setReferenceAmount] = useState<number>();
  const [ratio, setRatio] = useState<number>();
  const [ratioAngle, setRatioAngle] = useState<number>();
  const [isModalForecastInfoOpen, setIsModalForecastInfoOpen] = useState(false);
  const { t } = useTranslation();
  const setUserPreferences = useSetRecoilState(userPreferencesState);

  const chartData = {
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },

    chart: {
      backgroundColor: null,
      height: 300,
    },
    plotOptions: {
      series: {
        marker: {
          enabled: false,
        },
        legendSymbol: 'lineMarker',
        lineWidth: 15,
      },
    },
    yAxis: [],
    series: [
      {
        type: 'pie',
        enableMouseTracking: false,
        innerSize: '90%',
        dataLabels: {
          enabled: false,
        },
        data: [{ y: 0, color: '#efeeed' }],
        startAngle: 0.004,
      },
    ],
  };

  const [targetChartData, setTargetChartData] = useState({
    ...chartData,
  });

  const [currentChartData, setCurrentChartData] = useState<any>({
    ...chartData,
  });

  const [overChartData, setOverChartData] = useState<any>({
    ...chartData,
  });

  const isSameLastUpdate = useMemo(() => {
    if (budget && isFetching === false && isError === false) {
      const physicalMeter = headPointPhysicalMeter(sdp?.headpoint);
      const budgetEndDate = new Date(budget.data.endDate);
      if (physicalMeter?.lastAvailableDay != null) {
        const lastUpdate = new Date(physicalMeter.lastAvailableDay!);
        return (
          lastUpdate.getFullYear() === budgetEndDate.getFullYear() &&
          lastUpdate.getMonth() === budgetEndDate.getMonth() &&
          lastUpdate.getDay() === budgetEndDate.getDay()
        );
      }
      return false;
    }
    return false;
  }, [budget, isFetching, isError, sdp]);

  useEffect(() => {
    if (budget && isFetching === false && isError === false) {
      setFullyExceededBudget(
        isSameLastUpdate && budget.data.currentExpenses! > budget.data.referenceAmount!
      );
    }
  }, [budget, isFetching, isError, isSameLastUpdate]);

  useEffect(() => {
    if (budget && isFetching === false && isError === false) {
      setExceededBudget(
        !fullyExceededBudget && budget.data.currentExpenses! > budget.data.targetAmount! * 1.05
      );
    }
  }, [budget, isFetching, isError, fullyExceededBudget]);

  useEffect(() => {
    if (budget && isFetching === false && isError === false) {
      setReferenceAmount(Math.max(budget.data.currentExpenses!, budget.data.referenceAmount!));
    }
  }, [budget, isError, isFetching]);

  useEffect(() => {
    setRatio(1 / referenceAmount!);
    setRatioAngle(360 / referenceAmount!);
  }, [referenceAmount]);

  useEffect(() => {
    if (budget && isFetching === false && isError === false) {
      const targetPercentValue = Math.max(budget.data.currentExpenses! * ratio! * 100, 0);
      const overPercentValue =
        (budget.data.currentExpenses! - budget.data.targetAmount!) * ratio! * 100;
      const angle = budget.data.targetAmount! * ratioAngle!;
      setTargetChartData({
        ...targetChartData,
        series: [
          {
            ...targetChartData.series[0],
            startAngle: 0,
            data: [
              {
                y: targetPercentValue,
                color: fullyExceededBudget ? '#e88300' : '#9dca3b',
              },
              {
                y: 100 - targetPercentValue,
                color: '#efeeed',
              },
            ] as any,
          },
        ],
      });
      setOverChartData({
        ...overChartData,

        series: [
          {
            ...overChartData.series[0],
            startAngle: angle + 360,

            data: [
              {
                y: overPercentValue,

                color: '#E88300',
              },
              {
                y: 100 - overPercentValue,
                color: 'transparent',
              },
            ] as any,
          },
        ],
      });

      setCurrentChartData({
        ...currentChartData,

        series: [
          {
            ...currentChartData.series[0],
            startAngle: angle,

            data: [
              {
                y: 1,
                color: '#4B84E4',
              },
              {
                y: 99,
                color: 'transparent',
              },
            ] as any,
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget, isFetching, isError, exceededBudget, fullyExceededBudget, ratio, ratioAngle]);

  const renderTitle = () => (
    <span>
      {t(`overview.meter.detail.forecast.title.${period.toLowerCase()}`)}
      &nbsp;
      {period === PeriodEnum.YEARLY
        ? DateTime.fromISO(budget?.data.startDate!).toFormat('dd/MM/yyyy')
        : null}
    </span>
  );

  const handleClickInfo = () => {
    setIsModalForecastInfoOpen(true);
  };

  const renderIndicatorContent = () => (
    <div className="forecast-circular-chart__indicator">
      <div className="forecast-circular-chart__indicator__title">
        {unit === ConsumptionUnitEnum.EURO
          ? t('overview.meter.detail.forecast.expensed')
          : t('overview.meter.detail.forecast.consumed')}
      </div>
      <div className="forecast-circular-chart__indicator__value">
        <div
          className={classNames('forecast-circular-chart__indicator__value--actual', {
            'forecast-circular-chart__indicator__value--exceeded':
              exceededBudget || fullyExceededBudget,
          })}
        >
          {NumberUtils.parse(budget?.data.currentExpenses!)}
        </div>

        <div className="forecast-circular-chart__indicator__value--expected">
          &nbsp;/&nbsp;
          {NumberUtils.parse(budget?.data.referenceAmount!)}
          <span className="forecast-circular-chart__indicator__currency">
            {ConsumptionUnitEnum.getSymbol(unit)}
          </span>
        </div>
      </div>
      <div className="forecast-circular-chart__indicator__info">
        <InformationIcon
          className="icon--action icon--info"
          role="button"
          onClick={handleClickInfo}
        />
      </div>
    </div>
  );

  const getSituationComment = () => (exceededBudget || fullyExceededBudget ? 'bad' : 'good');

  const renderFooter = () => (
    <div className="forecast-circular-chart__footer">
      <div className="forecast-circular-chart__footer__prevision">
        <div className="forecast-circular-chart__footer__prevision__indicator"></div>
        {t('overview.meter.detail.forecast.prevision')}&nbsp;
        {DateTime.fromISO(headPointPhysicalMeter(sdp?.headpoint)?.lastAvailableDay!).toFormat(
          'dd/MM'
        )}
        &nbsp;:&nbsp;
        {NumberUtils.parse(budget?.data.targetAmount!)}
        &nbsp;
        {ConsumptionUnitEnum.getSymbol(unit)}
      </div>
      <div className="forecast-circular-chart__footer__advice">
        {t(`overview.meter.detail.forecast.${getSituationComment()}.comment.${unit.toLowerCase()}`)}
      </div>
      <div className="forecast-circular-chart__footer__remaining">
        {t(`overview.meter.detail.forecast.remain.${period.toLowerCase()}`, {
          remaining: NumberUtils.parse(
            budget?.data.referenceAmount! - budget?.data.currentExpenses!
          ),
          unit: ConsumptionUnitEnum.getSymbol(unit),
        })}
      </div>
      {headPointPhysicalMeter(sdp?.headpoint)?.lastAvailableDay && (
        <LastUpdateData
          date={new Date(headPointPhysicalMeter(sdp?.headpoint)?.lastAvailableDay!)}
        />
      )}
    </div>
  );

  const handleClickPeriod = (period: PeriodEnum) => {
    setUserPreferences((prev) => ({
      ...prev,
      periodGranularity: period,
    }));
  };

  const handleClickActionModalForecastInfo = () => {
    setIsModalForecastInfoOpen(false);
  };

  return (
    <div className="widget forecast-circular-chart">
      {isFetching || isError ? (
        <div className="forecast-circular-chart__skeleton">
          <Skeleton
            className="forecast-circular-chart__skeleton__item"
            width={300}
            height={300}
            borderRadius="100%"
          />
          <div className="forecast-circular-chart__skeleton__item__inner">
            <Skeleton className="forecast-circular-chart__skeleton__item" width={150} height={10} />
            <Skeleton className="forecast-circular-chart__skeleton__item" width={75} height={24} />
            <Skeleton className="forecast-circular-chart__skeleton__item" width={100} height={12} />
          </div>
        </div>
      ) : budget?.data == null || budget.status === 204 ? (
        <Fragment>
          <div className="forecast-circular-chart__period">
            <PeriodGranularity
              items={[
                { label: t('common.monthly'), value: PeriodEnum.MONTHLY },
                { label: t('common.yearly'), value: PeriodEnum.YEARLY },
              ]}
              value={period}
              onClick={handleClickPeriod}
              hasFullWidth
            />
          </div>
          <EmptyState message={t('common.no.data')} icon={<BarChartIcon />} />
        </Fragment>
      ) : (
        <Fragment>
          <div className="forecast-circular-chart__period">
            <PeriodGranularity
              items={[
                { label: t('common.monthly'), value: PeriodEnum.MONTHLY },
                { label: t('common.yearly'), value: PeriodEnum.YEARLY },
              ]}
              value={period}
              onClick={handleClickPeriod}
              hasFullWidth
            />
          </div>
          <div className="forecast-circular-chart__title">{renderTitle()}</div>
          <div className="forecast-circular-chart__container">
            {renderIndicatorContent()}
            <HgChart className="forecast-circular-chart__widget" chartData={targetChartData} />
            {exceededBudget && (
              <HgChart className="forecast-circular-chart__widget" chartData={overChartData} />
            )}
            <HgChart className="forecast-circular-chart__widget" chartData={currentChartData} />
          </div>
          {renderFooter()}
        </Fragment>
      )}
      <ModalForecastInformation
        isOpen={isModalForecastInfoOpen}
        sdp={sdp}
        onClickAction={handleClickActionModalForecastInfo}
      />
    </div>
  );
}
